import type { StateCreator } from 'zustand';
import { contactPoolApi, type PoolContact, type PoolStats, type PoolFilters } from '../services/contactPoolApi';

export interface ContactPoolSlice {
  poolContacts: PoolContact[];
  poolTotal: number;
  poolStats: PoolStats | null;
  poolLoading: boolean;
  poolError: string | null;
  poolFilters: PoolFilters;
  poolSelected: Set<number>;

  fetchPoolContacts: () => Promise<void>;
  fetchPoolStats: () => Promise<void>;
  reviewPoolContact: (id: number, action: 'accept' | 'reject') => Promise<void>;
  bulkReviewPoolContacts: (ids: number[], action: 'accept' | 'reject') => Promise<void>;
  promotePoolContact: (id: number, target_type: string, user_id?: number, client_id?: number | null) => Promise<void>;
  unpromotePoolContact: (id: number) => Promise<void>;
  bulkAssignClient: (ids: number[], client_id: number | null) => Promise<void>;
  reclassifyPoolContact: (id: number) => Promise<void>;
  bulkReclassifyPoolContacts: (ids: number[]) => Promise<void>;
  updatePoolContact: (id: number, data: Partial<PoolContact>) => Promise<void>;
  setPoolFilters: (filters: Partial<PoolFilters>) => void;
  setPoolSelected: (ids: Set<number>) => void;
}

const DEFAULT_FILTERS: PoolFilters = {
  status: null,
  category: null,
  source: null,
  client_id: null,
  website: null,
  search: '',
  sort_by: 'imported_at',
  sort_dir: 'desc',
  limit: 50,
  offset: 0,
};

export const createContactPoolSlice: StateCreator<ContactPoolSlice, [], [], ContactPoolSlice> = (set, get) => ({
  poolContacts: [],
  poolTotal: 0,
  poolStats: null,
  poolLoading: false,
  poolError: null,
  poolFilters: DEFAULT_FILTERS,
  poolSelected: new Set(),

  fetchPoolContacts: async () => {
    set({ poolLoading: true, poolError: null });
    try {
      const result = await contactPoolApi.listContacts(get().poolFilters);
      set({ poolContacts: result.items, poolTotal: result.total });
    } catch (e) {
      set({ poolError: (e as Error).message });
    } finally {
      set({ poolLoading: false });
    }
  },

  fetchPoolStats: async () => {
    try {
      const stats = await contactPoolApi.getStats();
      set({ poolStats: stats });
    } catch (e) {
      set({ poolError: (e as Error).message });
    }
  },

  reviewPoolContact: async (id, action) => {
    const updated = await contactPoolApi.review(id, action);
    set((s) => ({
      poolContacts: s.poolContacts.map((c) => (c.id === id ? updated : c)),
    }));
    get().fetchPoolStats();
  },

  bulkReviewPoolContacts: async (ids, action) => {
    await contactPoolApi.bulkReview(ids, action);
    await get().fetchPoolContacts();
    get().fetchPoolStats();
    set({ poolSelected: new Set() });
  },

  promotePoolContact: async (id, target_type, user_id, client_id) => {
    await contactPoolApi.promote(id, target_type, user_id, client_id);
    const updated = await contactPoolApi.getContact(id);
    set((s) => ({
      poolContacts: s.poolContacts.map((c) => (c.id === id ? updated : c)),
    }));
  },

  unpromotePoolContact: async (id) => {
    const updated = await contactPoolApi.unpromote(id);
    set((s) => ({
      poolContacts: s.poolContacts.map((c) => (c.id === id ? updated : c)),
    }));
    get().fetchPoolStats();
  },

  bulkAssignClient: async (ids, client_id) => {
    await contactPoolApi.bulkAssignClient(ids, client_id);
    await get().fetchPoolContacts();
    set({ poolSelected: new Set() });
  },

  reclassifyPoolContact: async (id) => {
    await contactPoolApi.reclassify(id);
  },

  bulkReclassifyPoolContacts: async (ids) => {
    await contactPoolApi.bulkReclassify(ids);
    set({ poolSelected: new Set() });
  },

  updatePoolContact: async (id, data) => {
    const updated = await contactPoolApi.updateContact(id, data);
    set((s) => ({
      poolContacts: s.poolContacts.map((c) => (c.id === id ? updated : c)),
    }));
  },

  setPoolFilters: (filters) => {
    set((s) => ({
      poolFilters: { ...s.poolFilters, ...filters, offset: 0 },
      poolSelected: new Set(),
    }));
    get().fetchPoolContacts();
  },

  setPoolSelected: (ids) => set({ poolSelected: ids }),
});
